import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ShieldCheck, ChevronDown } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/data/mockData";
import { useCampusLocations } from "@/hooks/useCampusLocations";
import { useUpcomingCourses } from "@/hooks/useUpcomingCourses";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();
  const { locations, loading: locationsLoading } = useCampusLocations();
  const { courses, loading: coursesLoading } = useUpcomingCourses();

  const upcomingCourse = courses?.[0] || null;

  const formatCourseDate = (value) => {
    if (!value) return "View available dates";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "View available dates";

    return new Intl.DateTimeFormat("en-AU", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  return (
    <>
      {/* Top strip */}
      <div className="bg-[#0A2540] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 opacity-90">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Nationally Accredited RTO #45698 · WorkSafe VIC approved</span>
          </div>
          <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-2 hover:text-orange-300 transition-colors" data-testid="topbar-phone">
            <Phone className="w-3.5 h-3.5" />
            <span className="font-semibold">{COMPANY.phone}</span>
          </a>
        </div>
      </div>

      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-lg bg-[#0A2540] flex items-center justify-center text-white font-extrabold text-sm">WC</div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-[#0A2540] text-lg">White Card VIC</div>
              <div className="text-[10px] text-slate-500 -mt-0.5 uppercase tracking-wider">Registered Training Organisation</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((n) => {
              if (n.label === "White Card Course") {
                return (
                  <div key={n.label} className="relative" onMouseEnter={() => setMegaOpen(true)} onMouseLeave={() => setMegaOpen(false)}>
                    <button className={`px-3.5 py-2 text-sm font-semibold flex items-center gap-1 rounded-md transition-colors ${location.pathname === n.to ? "text-[#EA580C]" : "text-slate-700 hover:text-[#0A2540]"}`} data-testid="nav-course-mega">
                      {n.label} <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    {megaOpen && (
                      <div className="absolute top-full left-0 w-[560px] bg-white border border-slate-200 rounded-xl shadow-xl p-6 grid grid-cols-2 gap-6 mt-1" data-testid="mega-menu">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Course</div>
                          {coursesLoading ? (
                            <div className="py-2 text-sm text-slate-500">Loading course...</div>
                          ) : upcomingCourse ? (
                            <>
                              <Link to="/course" className="block py-2 hover:text-[#EA580C]">
                                <div className="font-semibold">
                                  {upcomingCourse.courseName}
                                  {upcomingCourse.courseCode ? ` (${upcomingCourse.courseCode})` : ""}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {upcomingCourse.courseFee != null
                                    ? `$${Number(upcomingCourse.courseFee).toFixed(2)}`
                                    : "Fee available on booking"}
                                  {upcomingCourse.availableSeats != null
                                    ? ` · ${upcomingCourse.availableSeats} seats available`
                                    : ""}
                                </div>
                              </Link>

                              <Link to="/calendar" className="block py-2 hover:text-[#EA580C]">
                                <div className="font-semibold">Upcoming Classes</div>
                                <div className="text-xs text-slate-500">
                                  {formatCourseDate(upcomingCourse.scheduleDate)}
                                  {upcomingCourse.campusName ? ` · ${upcomingCourse.campusName}` : ""}
                                </div>
                              </Link>
                            </>
                          ) : (
                            <>
                              <Link to="/course" className="block py-2 hover:text-[#EA580C]">
                                <div className="font-semibold">White Card Course</div>
                                <div className="text-xs text-slate-500">Course details are currently unavailable</div>
                              </Link>
                              <Link to="/calendar" className="block py-2 hover:text-[#EA580C]">
                                <div className="font-semibold">Upcoming Classes</div>
                                <div className="text-xs text-slate-500">View the course calendar</div>
                              </Link>
                            </>
                          )}
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Locations</div>
                          {locationsLoading ? (
                            <div className="py-2 text-sm text-slate-500">Loading locations...</div>
                          ) : locations.length === 0 ? (
                            <div className="py-2 text-sm text-slate-500">No locations available</div>
                          ) : (
                            locations.map((campus) => {
                              const address = [campus.addressLine1, campus.suburb, campus.postCode]
                                .filter(Boolean)
                                .join(", ");

                              return (
                                <Link key={campus.campusId} to="/locations" className="block py-2 hover:text-[#EA580C]">
                                  <div className="font-semibold">{campus.campusName}</div>
                                  <div className="text-xs text-slate-500">{address}</div>
                                </Link>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={n.label}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) => `px-3.5 py-2 text-sm font-semibold rounded-md transition-colors ${isActive ? "text-[#EA580C]" : "text-slate-700 hover:text-[#0A2540]"}`}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-[#0A2540] px-3 py-2" data-testid="nav-login">Login</Link>
            <Link to="/calendar" className="wc-btn-primary text-sm" data-testid="nav-book-now">Book Now</Link>
          </div>

          <button onClick={() => setOpen(!open)} className="lg:hidden p-2" aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-1">
              {NAV_LINKS.map((n) => (
                <NavLink key={n.label} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)}
                  className={({ isActive }) => `block px-3 py-2.5 rounded-md text-sm font-semibold ${isActive ? "bg-orange-50 text-[#EA580C]" : "text-slate-700 hover:bg-slate-50"}`}>
                  {n.label}
                </NavLink>
              ))}
              <div className="pt-3 mt-2 border-t border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Locations</div>
                {locationsLoading ? (
                  <div className="px-3 py-2 text-sm text-slate-500">Loading locations...</div>
                ) : locations.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-500">No locations available</div>
                ) : (
                  <div className="space-y-1 px-1 pb-2">
                    {locations.map((campus) => {
                      const address = [campus.addressLine1, campus.suburb, campus.postCode]
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <Link key={campus.campusId} to="/locations" onClick={() => setOpen(false)} className="block rounded-md px-3 py-2 hover:bg-slate-50">
                          <div className="text-sm font-semibold text-slate-700">{campus.campusName}</div>
                          <div className="text-xs text-slate-500">{address}</div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="pt-3 mt-2 border-t border-slate-200 flex gap-2">
                <Link to="/login" onClick={() => setOpen(false)} className="flex-1 wc-btn-outline text-sm text-center">Login</Link>
                <Link to="/calendar" onClick={() => setOpen(false)} className="flex-1 wc-btn-primary text-sm justify-center">Book Now</Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
